import { Immutable } from "immer";
import React from "react";

import type { TSIAlignedRichTextBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useLazyBackground } from "../../utils/lazyBackground";
import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/aligned-rich-text-block.html``. Mirrors the inner
 * ``AlignedRichTextBlock`` renderer already screenshot-proven inside
 * ``PromotionBlock`` (#33583): a full ``<section>`` with the lazy-background
 * data attributes consumed by the global lazy-background widget. We render the
 * STATIC initial DOM only (no React state).
 *
 * Two intentional details:
 *   - ``data-background-image`` uses the raw ``background_image.url`` (mirroring
 *     PromotionBlock), NOT the ``width-2000`` rendition named in the Django
 *     template; PromotionBlock's choice is the screenshot-proven one.
 *   - Spacing uses ``getGlobalSpacingClasses`` (the GLOBAL class strings emitted
 *     by ``spacing-sub-block.html``), NOT the CSS-module ``getSpacingClasses``,
 *     so the SSR output matches the legacy server render's global SCSS.
 */
export const TSIAlignedRichTextBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const sectionRef = React.useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);
    const bgImage = v.background_style?.background_image;
    const bgUrl = bgImage?.url ?? "";
    const bgHeight = bgImage?.height ?? "";
    const shiftJson = JSON.stringify(
        v.background_style?.background_image_shift ?? null,
    );
    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "aligned-rich-text-block",
                v.alignment ?? "",
                v.full_width ? "aligned-rich-text-block--full-width" : "",
                v.background_style?.style ?? "",
                getGlobalSpacingClasses(v.spacing),
                bgUrl ? "background-image" : "",
                "lazy-background",
            ])}
            data-background-image={bgUrl}
            data-background-image-height={bgHeight}
            data-background-image-shift={shiftJson}
        >
            <RichTextBlock value={v.content ?? ""} />
        </section>
    );
};
